import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Target, TrendingUp } from 'lucide-react';
import type { OportunidadeListItem } from '../api';
import {
  ConfidencialBadge,
  OportunidadePrioridadeBadge,
  OportunidadeStatusBadge,
  StalledBadge,
} from './OportunidadeBadges';
import { formatCurrencyBRL, formatDateBR } from '../utils/format';

interface Props {
  card: OportunidadeListItem;
  stageLabel?: string;
}

/**
 * Card compacto reutilizável nas visões Cards e (potencialmente) em
 * drawers/side-panels. Clique navega para o detalhe. Sinais de saúde
 * (confidencial, parada) ficam no header à direita para leitura rápida.
 */
export const OportunidadeCard: React.FC<Props> = ({ card, stageLabel }) => {
  const navigate = useNavigate();
  return (
    <article
      onClick={() => navigate(`/oportunidades/${card.id}`)}
      className="flex cursor-pointer flex-col gap-2 rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="font-mono text-[10px] text-gray-500">
          {card.codigo ?? 'RASCUNHO'}
        </span>
        <div className="flex flex-wrap items-center gap-1">
          {card.isConfidential && <ConfidencialBadge />}
          {card.isStalled && <StalledBadge />}
          <OportunidadePrioridadeBadge prioridade={card.prioridade} />
        </div>
      </div>
      <h3 className="line-clamp-2 text-sm font-semibold text-gray-900">{card.nome}</h3>
      {stageLabel && (
        <p className="text-xs text-gray-500">Etapa: {stageLabel}</p>
      )}
      <div className="mt-1 flex items-center gap-3 text-xs text-gray-600">
        <span className="inline-flex items-center gap-1 tabular-nums">
          <Target className="h-3 w-3 text-gray-400" />
          {formatCurrencyBRL(card.valorEstimado)}
        </span>
        {card.valorPonderado && (
          <span className="inline-flex items-center gap-1 tabular-nums text-gray-500">
            <TrendingUp className="h-3 w-3 text-gray-400" />
            {formatCurrencyBRL(card.valorPonderado)}
          </span>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 pt-1 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {formatDateBR(card.expectedClosingDate)}
        </span>
        <OportunidadeStatusBadge status={card.status} />
      </div>
    </article>
  );
};
